// Phase 0091 — Attendance Derivation page.
// Manual trigger surface for derive_attendance_from_punches. Filters by
// date / shift / department / employee. Shows result summary cards.

import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, DatePicker, Form, Input, Row, Select,
  Space, Statistic, Typography,
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'

import { hrAutomationApi } from '@/features/hr/schedulerApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { employeesApi } from '@/features/hr/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Title } = Typography

export default function AttendanceDerivationPage() {
  const { message: msg } = App.useApp()
  const { plantId } = usePlantId()
  const [date, setDate] = useState<Dayjs>(dayjs())
  const [departmentId, setDepartmentId] = useState<string | undefined>()
  const [employeeId, setEmployeeId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [result, setResult] = useState<Awaited<ReturnType<typeof hrAutomationApi.deriveAttendance>> | null>(null)

  const { data: depts } = useQuery({
    queryKey: ['depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 100, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: employees } = useQuery({
    queryKey: ['emps', plantId, departmentId],
    queryFn: () => employeesApi.list({
      plant_id: plantId, department_id: departmentId,
      page: 1, size: 200,
    }),
    enabled: !!plantId,
  })

  const deriveMut = useMutation({
    mutationFn: () => hrAutomationApi.deriveAttendance({
      target_date: date.format('YYYY-MM-DD'),
      shift_id: shiftId,
      employee_id: employeeId,
    }),
    onSuccess: (data) => {
      setResult(data)
      msg.success(
        `Processed ${data.employees_processed} employees: ` +
        `${data.rows_created} created, ${data.rows_updated} updated, ` +
        `${data.rows_flagged_review} need review`)
    },
    onError: (e: Error) => msg.error(e.message),
  })

  return (
    <>
      <PageHeader
        title="Derive Attendance from Punches"
        subtitle="Reads punch_logs, applies attendance and OT rules, upserts daily_attendance rows. Idempotent."
      />

      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="HR should not manually enter attendance when punch data exists"
        description={
          <>
            The nightly scheduler runs this at 23:30 IST automatically. This page is
            for one-off re-runs or back-filling specific dates / shifts /
            employees. The service is safe to re-run; rows are upserted and HR
            overrides are preserved.
          </>
        }
      />

      <Card title="Derivation filters" style={{ marginBottom: 16 }}>
        <Form layout="vertical">
          <Row gutter={12}>
            <Col xs={12} md={6}>
              <Form.Item label="Date">
                <DatePicker
                  value={date}
                  onChange={(d) => d && setDate(d)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Department (optional)">
                <Select
                  allowClear showSearch
                  placeholder="All departments"
                  value={departmentId}
                  onChange={(v) => { setDepartmentId(v); setEmployeeId(undefined) }}
                  options={(depts?.items ?? []).map((d) => ({
                    value: d.id, label: d.name,
                  }))}
                  filterOption={(input, opt) =>
                    ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Employee (optional)">
                <Select
                  allowClear showSearch
                  placeholder="All employees in dept"
                  value={employeeId}
                  onChange={setEmployeeId}
                  options={(employees?.items ?? []).map((e) => ({
                    value: e.id,
                    label: `${e.emp_name} (${e.emp_code})`,
                  }))}
                  filterOption={(input, opt) =>
                    ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Shift (optional UUID)">
                <Input
                  placeholder="Paste shift UUID"
                  value={shiftId || ''}
                  onChange={(e) => setShiftId(e.target.value || undefined)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" size="large"
                    onClick={() => deriveMut.mutate()}
                    loading={deriveMut.isPending}>
              Derive Attendance
            </Button>
            <Text type="secondary">
              {employeeId ? 'One employee' : (departmentId ? 'Department scope' : 'All active employees')}
            </Text>
          </Space>
        </Form>
      </Card>

      {result && (
        <Card title="Last run result">
          <Row gutter={[12, 12]}>
            <Col xs={12} md={4}>
              <Card size="small"><Statistic title="Processed" value={result.employees_processed} /></Card>
            </Col>
            <Col xs={12} md={4}>
              <Card size="small">
                <Statistic title="Created" value={result.rows_created}
                            valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card size="small">
                <Statistic title="Updated" value={result.rows_updated}
                            valueStyle={{ color: '#1677ff' }} />
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card size="small">
                <Statistic title="Need review" value={result.rows_flagged_review}
                            valueStyle={{ color: '#fa8c16' }} />
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card size="small">
                <Statistic title="Skipped" value={result.rows_skipped} />
              </Card>
            </Col>
          </Row>
          {result.rows_flagged_review > 0 && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning" showIcon
              message={`${result.rows_flagged_review} row(s) flagged for manual review`}
              description="Rows with missing IN/OUT punch or OUT-before-IN are not auto-marked PRESENT. Open Daily Attendance to resolve."
            />
          )}
        </Card>
      )}
    </>
  )
}
